import React, { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Typography, Grid, Chip, MenuItem } from "@mui/material";
import SVGSimpleCheckMark from "@/assets/icons/SVGSimpleCheckMark.tsx";

import FormInput from "@/components/FormInput";
import FormNumberInput from "@/components/FormNumberInput";
import FormSelect from "@/components/FormSelect";
import FormCheckboxGroup from "@/components/FormCheckboxGroup";
import FormCheckbox from "@/components/FormCheckbox";
import FormCurrencyInput from "@/components/FormCurrencyInput";
import FormFileUpload from "@/components/FormUploadInput";
import FormDatePicker from "@/components/FormDatePicker";
import AsyncSelectWithSearch from "@/components/AsyncSelectWithSearch";
import { InfoIcon } from "@/components/InfoIcon";
import { useUserStore } from "@/store/userStore.ts";
import { useWatch } from "react-hook-form";
import { getBatteryOemsReq, getContractorsReq } from "@/api/userApi";
import { getBatteryListReq } from "@/api/batteryApi";
import { getUserReq } from "@/api/adminApi";
import { useAuthStore } from "@/store/authStore.ts";
import { numberInputKeyDownValidation } from "@/utils/numberInputKeyDownValidation.ts";
import { validatePhone } from "@/utils/phoneValidation.ts";
import {
  calculateTotalNominalPower,
  calculateTotalUsableCapacity,
} from "@/utils/calculateTotalNominalPower.ts";
import { PHONE_NUMBER_REGEX } from "@/constants/phoneValidation";
import { EmailPatternValidation } from "@/validators/emailPatternValidation";
import { ProjectFormMode } from "@/constants";
import useIncentiveImpactedFields from "@/hooks/useIncentiveImpactedFields";
import RecalculateIncentiveModal from "@/components/RecalculateIncentiveModal";
import IncentiveImpactHighlight from "@/components/IncentiveImpactHighlight";

type Props = {
  mode?: ProjectFormMode;
  /** Present only when editing — recalculation needs a saved project. */
  projectId?: number;
};

const SystemInformationStep = ({ mode = "create", projectId }: Props) => {
  const { watch, control, setValue, getValues, trigger } = useFormContext();

  const role = useAuthStore((state) => state.role);
  const isContractor = role === "contractor";
  const isAdministrator = role === "administrator";

  /**
   * Highlights, warnings and manual recalculation are administrator-only — contractors see
   * none of it (client request, 2026-08-18). Gating the hook itself means a contractor gets no
   * impacted fields at all, so there is no unexplained highlight without its warning.
   */
  const canSeeIncentiveWarnings = mode === "edit" && isAdministrator;

  const { isFieldImpacted } = useIncentiveImpactedFields(
    canSeeIncentiveWarnings
  );

  const externalIncentivesValue = watch("externalIncentives");
  const totalProjectCostValue = watch("totalProjectCost");

  /** Recalculating also needs a saved project, so the control is absent while creating one. */
  const canRecalculate = canSeeIncentiveWarnings && Boolean(projectId);

  // The form holds the incentive in dollars; the recalculate API speaks cents.
  const storedIncentiveInCents = Math.round(
    (Number(watch("upfrontIncentivePayment")) || 0) * 100
  );

  /**
   * Push the recalculated amount back into the form so the Upfront Incentive Amount on step 3
   * shows the new figure straight away, rather than the value loaded when the modal opened.
   */
  const handleRecalculated = (newIncentiveInCents: number) => {
    setValue(
      "upfrontIncentivePayment",
      (Number(newIncentiveInCents) || 0) / 100,
      { shouldDirty: false }
    );
  };

  const user = useUserStore((state) => state.user);

  const batteryOemId = watch("batteryOemId");
  const batteryProductName = watch("batteryProductName");
  const batteryQuantity = watch("batteryQuantity");
  const usableCapacity = watch("usableCapacity");
  const usableCapacityIncentive = watch("usableCapacityIncentive");
  const nominalPower = watch("nominalPower");
  const solarSystemType = watch("solarSystemType");

  const totalCapacityIncentive = calculateTotalUsableCapacity(
    usableCapacityIncentive,
    batteryQuantity
  );
  const totalNominalPower = calculateTotalNominalPower(
    nominalPower,
    batteryQuantity
  );
  const [selectedOemRequiresTcs, setSelectedOemRequiresTcs] =
    useState<boolean>(false);

  const hasExistingSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("existing");
  const hasNewSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("new");

  const createSolarFieldValidator = (systemType: "existing" | "new") => {
    const isSelected =
      systemType === "existing" ? hasExistingSolar : hasNewSolar;
    const systemName = systemType === "existing" ? "Existing" : "New";

    return (value: number) => {
      if (isSelected && (value === undefined || value === null || value <= 0)) {
        return `Required when ${systemName} Solar System is selected`;
      }
      return true;
    };
  };

  useEffect(() => {
    if (isContractor && user) {
      setValue("contractor", user.fullName || user.companyName || "");
      setValue("contractorId", user.id?.toString() || "");
    }
  }, [isContractor, user, setValue]);

  const getContractors = useCallback(
    (searchValue: string) => getContractorsReq(searchValue),
    []
  );

  const getBatteryOems = useCallback(
    (searchValue: string) => getBatteryOemsReq(searchValue),
    []
  );

  const getBatteryList = useCallback(
    async (searchValue: string) => {
      try {
        const response = await getBatteryListReq(batteryOemId, searchValue);
        return response;
      } catch (error) {
        return { data: { batteryModels: [] } };
      }
    },
    [batteryOemId]
  );

  useEffect(() => {
    if (!batteryOemId || !batteryProductName) {
      return;
    }

    const fetchBatteryData = async () => {
      try {
        if (isContractor) {
          const batteryResponse = await getBatteryListReq(batteryOemId, "");
          const batteries = batteryResponse?.data?.batteryModels || [];
          const found = batteries.find(
            (b: any) => b.name === batteryProductName
          );

          if (found?.totalCapacity) {
            setValue("usableCapacity", found.totalCapacity);
          } else if (found?.totalCapacityKwh) {
            setValue("usableCapacity", found.totalCapacityKwh);
          } else {
            setValue("usableCapacity", "");
          }

          if (found?.totalCapacityIncentiveKwh) {
            setValue(
              "usableCapacityIncentive",
              found.totalCapacityIncentiveKwh
            );
          } else {
            setValue("usableCapacityIncentive", "");
          }

          // Set nominal power
          if (found?.nominalPowerKw) {
            setValue("nominalPower", found.nominalPowerKw);
          } else if (found?.nominalPower) {
            setValue("nominalPower", found.nominalPower);
          } else {
            setValue("nominalPower", "");
          }

          const requiresTcs = found?.termsAndConditions || false;
          setSelectedOemRequiresTcs(requiresTcs);
          setValue("requiresTermsAndConditions", requiresTcs);
        } else {
          const oemResp = await getUserReq(batteryOemId);
          const userData = oemResp?.data?.userData;
          const batteries: Array<{
            batteryModel: string;
            totalCapacityKwh?: string;
            totalCapacity?: string;
            totalCapacityIncentiveKwh?: string;
            nominalPowerKw?: string;
            nominalPower?: string;
            termsAndConditions?: boolean;
          }> = userData?.battery || [];
          const found = batteries.find(
            (b) => b.batteryModel === batteryProductName
          );

          if (found?.totalCapacity) {
            setValue("usableCapacity", found.totalCapacity);
          } else if (found?.totalCapacityKwh) {
            setValue("usableCapacity", found.totalCapacityKwh);
          }

          if (found?.totalCapacityIncentiveKwh) {
            setValue(
              "usableCapacityIncentive",
              found.totalCapacityIncentiveKwh
            );
          } else {
            setValue("usableCapacityIncentive", "");
          }

          if (found?.nominalPowerKw) {
            setValue("nominalPower", found.nominalPowerKw);
          } else if (found?.nominalPower) {
            setValue("nominalPower", found.nominalPower);
          }
          const requiresTcs = found?.termsAndConditions || false;
          setSelectedOemRequiresTcs(requiresTcs);
          setValue("requiresTermsAndConditions", requiresTcs);
        }
      } catch (error) {
        console.error("Failed to fetch battery data:", error);
      }
    };
    fetchBatteryData();
  }, [batteryOemId, batteryProductName, setValue, isContractor]);

  useEffect(() => {
    if (!batteryOemId || !batteryProductName) {
      setSelectedOemRequiresTcs(false);
      setValue("requiresTermsAndConditions", false);
      return;
    }

    const fetchBatteryTermsData = async () => {
      try {
        if (isContractor) {
          const batteryResponse = await getBatteryListReq(batteryOemId, "");
          const batteries = batteryResponse?.data?.batteryModels || [];
          const found = batteries.find(
            (b: any) => b.name === batteryProductName
          );
          const requiresTcs = found?.termsAndConditions || false;
          setSelectedOemRequiresTcs(requiresTcs);
          setValue("requiresTermsAndConditions", requiresTcs);
        } else {
          const oemResp = await getUserReq(batteryOemId);
          const userData = oemResp?.data?.userData;
     
          
          const requiresTcs = userData?.termsAndConditions || false;
          setSelectedOemRequiresTcs(requiresTcs);
          setValue("requiresTermsAndConditions", requiresTcs);
        }
      } catch (error) {
        console.error(
          "Failed to fetch battery terms & conditions requirement:",
          error
        );
        setSelectedOemRequiresTcs(false);
      }
    };
    fetchBatteryTermsData();
  }, [batteryOemId, batteryProductName, isContractor, setValue]);

  useEffect(() => {
    const numberOfBatteries = Number(batteryQuantity);
    const nameplateCapacity = Number(usableCapacity);

    if (numberOfBatteries > 0 && nameplateCapacity > 0) {
      const calculatedTotalCapacity = numberOfBatteries * nameplateCapacity;
      setValue("totalCapacity", calculatedTotalCapacity.toString());
    } else {
      setValue("totalCapacity", "");
    }
  }, [batteryQuantity, usableCapacity, setValue]);

  useEffect(() => {
    const numberOfBatteries = Number(batteryQuantity);
    const nominalPowerValue = Number(nominalPower);

    if (numberOfBatteries > 0 && nominalPowerValue > 0) {
      const calculatedTotalNominalPower = numberOfBatteries * nominalPowerValue;
      setValue("totalNominalPower", calculatedTotalNominalPower.toString());
    } else {
      setValue("totalNominalPower", "");
    }
  }, [batteryQuantity, nominalPower, setValue]);

  const careEligible = useWatch({ control, name: "careEligible" });
  const feraEligible = useWatch({ control, name: "feraEligible" });
  const cocEligible = useWatch({ control, name: "cocEligible" });

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#373533",
                  width: "120px",
                  flexShrink: 0,
                }}
              >
                Account Status
              </Typography>
              <Chip
                label="Account Active"
                sx={{
                  backgroundColor: "#CEEED9",
                  color: "#4caf50",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: "24px",
                  "& .MuiChip-label": {
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  },
                }}
                icon={
                  <SVGSimpleCheckMark color="#4caf50" width="12" height="12" />
                }
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#373533",
                  width: "120px",
                  flexShrink: 0,
                }}
              >
                CARE Participation
              </Typography>
              <Chip
                label={careEligible ? "Yes" : "No"}
                sx={{
                  backgroundColor: careEligible ? "#CEEED9" : "#F5F5F5",
                  color: careEligible ? "#4caf50" : "#666",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: "24px",
                }}
                icon={
                  <SVGSimpleCheckMark
                    color={careEligible ? "#4caf50" : "#666"}
                    width="12"
                    height="12"
                  />
                }
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#373533",
                  width: "120px",
                  flexShrink: 0,
                }}
              >
                Community of Concern
              </Typography>
              <Chip
                label={cocEligible ? "Yes" : "No"}
                sx={{
                  backgroundColor: cocEligible ? "#CEEED9" : "#F5F5F5",
                  color: cocEligible ? "#4caf50" : "#666",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: "24px",
                  "& .MuiChip-label": {
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  },
                }}
                icon={
                  <SVGSimpleCheckMark
                    color={cocEligible ? "#4caf50" : "#666"}
                    width="12"
                    height="12"
                  />
                }
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#373533",
                  width: "120px",
                  flexShrink: 0,
                }}
              >
                FERA Participation
              </Typography>
              <Chip
                label={feraEligible ? "Yes" : "No"}
                sx={{
                  backgroundColor: feraEligible ? "#CEEED9" : "#F5F5F5",
                  color: feraEligible ? "#4caf50" : "#666",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: "24px",
                }}
                icon={
                  <SVGSimpleCheckMark
                    color={feraEligible ? "#4caf50" : "#666"}
                    width="12"
                    height="12"
                  />
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"phoneNumber"}
            control={control}
            label={"Phone Number"}
            placeholder={"Enter phone number"}
            controlProps={{
              rules: {
                validate: validatePhone,
              },
            }}
            inputProps={{
              maxLength: 14,
              onKeyPress: (e) => {
                if (!PHONE_NUMBER_REGEX.test(e.key)) {
                  e.preventDefault();
                }
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"accountEmail"}
            control={control}
            label={"Customer Email"}
            placeholder={"Enter customer email"}
            controlProps={{
              rules: {
                required: "Required",
                ...EmailPatternValidation,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"streetAddress"}
            control={control}
            label={"Street Address"}
            placeholder={"Enter street address"}
            controlProps={{ rules: { required: "Required" } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"city"}
            control={control}
            label={"City"}
            placeholder={"Enter city"}
            controlProps={{ rules: { required: "Required" } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"state"}
            control={control}
            label={"State"}
            placeholder={"Enter state"}
            controlProps={{ rules: { required: "Required" } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormInput
            name={"zipCode"}
            control={control}
            label={"Zip Code"}
            placeholder={"Enter zip code"}
            controlProps={{ rules: { required: "Required" } }}
          />
        </Grid>
      </Grid>

      <FormCheckboxGroup
        name={"solarSystemType"}
        control={control}
        label="Solar Systems"
        multiple={true}
        items={[
          { value: "existing", label: "Existing Solar System" },
          { value: "new", label: "New Solar System" },
        ]}
        controlProps={{ rules: { required: "Required" } }}
      />

      {hasExistingSolar && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box>
              <FormNumberInput
                name={"solarKwdc"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Existing Solar System: kW-DC
                    <InfoIcon tooltipText="Enter the capacity of your existing solar system. Enter 0 if no existing solar system." />
                  </Box>
                }
                placeholder={"Enter kW-DC"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0, step: 0.01 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasExistingSolar ? "Required" : false,
                    validate: createSolarFieldValidator("existing"),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <FormNumberInput
                name={"solarKwac"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Existing Solar System: kW-AC
                    <InfoIcon tooltipText="Enter the capacity of your existing solar system. Enter 0 if no existing solar system." />
                  </Box>
                }
                placeholder={"Enter kW-AC"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0, step: 0.01 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasExistingSolar ? "Required" : false,
                    validate: createSolarFieldValidator("existing"),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <FormNumberInput
                name={"solarAnnualProduction"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Estimated Annual Solar Production (kWh)
                    <InfoIcon tooltipText="Enter the capacity of your existing solar system. Enter 0 if no existing solar system." />
                  </Box>
                }
                placeholder={"Enter annual production (kWh)"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasExistingSolar ? "Required" : false,
                    validate: createSolarFieldValidator("existing"),
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {hasNewSolar && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box>
              <FormNumberInput
                name={"newSolarKwdc"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    New Solar System: kW-DC
                    <InfoIcon tooltipText="Enter the capacity of your new solar system. Enter 0 if no new solar system." />
                  </Box>
                }
                placeholder={"Enter kW-DC"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0, step: 0.01 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasNewSolar ? "Required" : false,
                    validate: createSolarFieldValidator("new"),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <FormNumberInput
                name={"newSolarKwac"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    New Solar System: kW-AC
                    <InfoIcon tooltipText="Enter the capacity of your new solar system. Enter 0 if no new solar system." />
                  </Box>
                }
                placeholder={"Enter kW-AC"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0, step: 0.01 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasNewSolar ? "Required" : false,
                    validate: createSolarFieldValidator("new"),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <FormNumberInput
                name={"newSolarAnnualProduction"}
                control={control}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Estimated Annual Solar Production (kWh)
                    <InfoIcon tooltipText="Enter the capacity of your new solar system. Enter 0 if no new solar system." />
                  </Box>
                }
                placeholder={"Enter annual production (kWh)"}
                onKeyDown={numberInputKeyDownValidation}
                inputProps={{ min: 0 }}
                autoFormat={true}
                controlProps={{
                  rules: {
                    required: hasNewSolar ? "Required" : false,
                    validate: createSolarFieldValidator("new"),
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/*
        SDCP-1310: the recalc warning now renders at the bottom of the modal, just above the
        wizard's action buttons (in MultiStepProjectForm) — client request. The Recalculate
        control below stays inline with the fields. Administrator-only; recalculation is
        explicit-click only (SDCP-1305 removed auto-recalc that silently restated amounts).
      */}
      {canRecalculate && (
        <Box
          data-testid="recalculate-incentive-row"
          sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}
        >
          <RecalculateIncentiveModal
            projectId={projectId as number}
            currentIncentive={storedIncentiveInCents}
            onSuccess={handleRecalculated}
          />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormSelect
            name={"externalIncentivePrograms"}
            control={control}
            label={"External Incentive Programs Leveraged"}
            valueLabelMap={{
              "dac-sash": "DAC-SASH",
              sgip: "SGIP",
              "city-solar-equity": "City of San Diego's Solar Equity Program",
              other: "Other",
              none: "None",
            }}
            controlProps={{
              rules: {
                required: "Required",
                validate: (v: string) =>
                  (v && v.trim().length > 0) || "Required",
              },
            }}
          >
            <MenuItem value="dac-sash">DAC-SASH</MenuItem>
            <MenuItem value="sgip">SGIP</MenuItem>
            <MenuItem value="city-solar-equity">
              City of San Diego's Solar Equity Program
            </MenuItem>
            <MenuItem value="other">Other</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </FormSelect>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <IncentiveImpactHighlight
            impacted={isFieldImpacted("externalIncentives")}
          >
            <FormCurrencyInput
              name={"externalIncentives"}
              control={control}
              label={"External Incentives"}
              placeholder={"Enter amount"}
              value={externalIncentivesValue || ""}
              helperText={
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#666",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Include other incentives leveraged for the project (e.g. SGIP,
                  DAC-SASH, City of San Diego Solar Equity Program). Do not
                  include tax credits or refunds.
                </Typography>
              }
            />
          </IncentiveImpactHighlight>
        </Grid>
        <Grid item xs={12} sx={{ pt: 0 }}>
          <FormCheckbox
            value={watch("thirdPartyOwned") || false}
            onChange={(value: boolean) => setValue("thirdPartyOwned", value)}
            label="Will the system be owned by a Third-Party?"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          {isContractor ? (
            <FormInput
              name={"contractor"}
              control={control}
              label={"Contractor"}
              disabled={true}
              controlProps={{ rules: { required: "Required" } }}
            />
          ) : (
            <AsyncSelectWithSearch
              name={"contractor"}
              idKey={"contractorId"}
              control={control}
              label={"Contractor"}
              getItems={getContractors}
              itemsKey={"contractors"}
              placeholder={"Select contractor"}
              controlProps={{ rules: { required: "Required" } }}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormSelect
            name={"inverterManufacturer"}
            control={control}
            label={"Inverter Manufacturer"}
          >
            <MenuItem value="tesla">Tesla</MenuItem>
            <MenuItem value="enphase">Enphase</MenuItem>
            <MenuItem value="franklinwh">FranklinWH</MenuItem>
            <MenuItem value="solaredge">SolarEdge</MenuItem>
            <MenuItem value="delta electronics">Delta Electronics</MenuItem>
            <MenuItem value="schneider electric">Schneider Electric</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </FormSelect>
        </Grid>
        <Grid item xs={12}>
          <FormSelect
            name={"inverterName"}
            control={control}
            label={"Inverter Name"}
          >
            <MenuItem value="integrated">Integrated</MenuItem>
            <MenuItem value="se3800h-us">SE3800H-US</MenuItem>
            <MenuItem value="se6000h-us">SE6000H-US</MenuItem>
            <MenuItem value="se7600h-us">SE7600H-US</MenuItem>
            <MenuItem value="se10000h-us">SE10000H-US</MenuItem>
            <MenuItem value="e6-tl-us">E6-TL-US</MenuItem>
            <MenuItem value="e4-tl-us">E4-TL-US</MenuItem>
            <MenuItem value="xwpro6848na">XWPro6848NA</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </FormSelect>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <IncentiveImpactHighlight
            impacted={isFieldImpacted("batteryManufacturer")}
          >
            <AsyncSelectWithSearch
              name={"batteryManufacturer"}
              idKey={"batteryOemId"}
              control={control}
              disabled={role === "oem"}
              label={"Battery Manufacturer"}
              getItems={getBatteryOems}
              itemsKey={"oems"}
              placeholder={"Select battery manufacturer"}
              controlProps={{
                rules: { required: "Required" },
              }}
              onValueChange={() => {
                setValue("batteryProductName", "");
                setValue("batteryModelId", "");
                setValue("usableCapacity", "");
                setValue("nominalPower", "");
              }}
            />
          </IncentiveImpactHighlight>
        </Grid>
        <Grid item xs={12}>
          <IncentiveImpactHighlight
            impacted={isFieldImpacted("batteryProductName")}
          >
            <AsyncSelectWithSearch
              name={"batteryProductName"}
              idKey={"batteryModelId"}
              control={control}
              label={"Battery Product Name"}
              getItems={getBatteryList}
              itemsKey={"batteryModels"}
              placeholder={"Select battery product"}
              disabled={!getValues("batteryManufacturer")}
              controlProps={{
                rules: { required: "Required" },
              }}
              onValueChange={() => {
                const updatedValues = getValues();

                const missingFields = [];
                if (!updatedValues.contractor) missingFields.push("contractor");
                if (
                  !updatedValues.batteryQuantity ||
                  (typeof updatedValues.batteryQuantity === "string" &&
                    updatedValues.batteryQuantity.trim() === "")
                )
                  missingFields.push("batteryQuantity");

                if (
                  updatedValues.batteryOemId &&
                  updatedValues.batteryProductName
                ) {
                  if (
                    updatedValues.batteryOemId &&
                    updatedValues.batteryProductName
                  ) {
                    const fetchCapacity = async () => {
                      try {
                        if (isContractor) {
                          const batteryResponse = await getBatteryListReq(
                            updatedValues.batteryOemId,
                            ""
                          );

                          const batteries =
                            batteryResponse?.data?.batteryModels || [];
                          const found = batteries.find(
                            (b: any) =>
                              b.name === updatedValues.batteryProductName
                          );

                          if (found?.totalCapacity) {
                            setValue("usableCapacity", found.totalCapacity);
                          } else if (found?.totalCapacityKwh) {
                            setValue("usableCapacity", found.totalCapacityKwh);
                          } else {
                          }

                          if (found?.nominalPowerKw) {
                            setValue("nominalPower", found.nominalPowerKw);
                          } else if (found?.nominalPower) {
                            setValue("nominalPower", found.nominalPower);
                          }
                        } else {
                          // Not a contractor, skipping capacity fetch
                        }
                      } catch (error) {
                        console.error("Manual fetch failed:", error);
                      }
                    };

                    fetchCapacity();
                  }
                }
              }}
            />
          </IncentiveImpactHighlight>
        </Grid>

        <Grid item xs={12}>
          <IncentiveImpactHighlight
            impacted={isFieldImpacted("batteryQuantity")}
          >
            <FormInput
              type={"number"}
              name={"batteryQuantity"}
              control={control}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Battery Quantity
                  <InfoIcon tooltipText="Number of battery units to be installed" />
                </Box>
              }
              placeholder={"Enter quantity"}
              onKeyDown={numberInputKeyDownValidation}
              inputProps={{ min: 1, step: 1 }}
              controlProps={{
                rules: {
                  required: "Battery quantity is required",
                  validate: {
                    positiveNumber: (value) => {
                      const num = Number(value);
                      if (isNaN(num) || num <= 0) {
                        return "Battery quantity must be a positive number";
                      }
                      if (!Number.isInteger(num)) {
                        return "Battery quantity must be a whole number";
                      }
                      return true;
                    },
                  },
                },
              }}
              onBlur={() => {
                trigger("batteryQuantity");
              }}
            />
          </IncentiveImpactHighlight>
        </Grid>
        <Grid item xs={12}>
          <FormInput
            name={"usableCapacity"}
            control={control}
            label={"Nameplate Usable Capacity (kWh)"}
            disabled={true}
          />
        </Grid>
        {!isContractor && (
          <Grid item xs={12}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 500, color: "#373533" }}
                >
                  Total Capacity Incentive (kWh)
                </Typography>
                <InfoIcon tooltipText="Automatically calculated: Number of Batteries × Capacity Incentive" />
              </Box>
              <FormInput
                name={"totalCapacityIncentive"}
                control={control}
                label={""}
                value={totalCapacityIncentive}
                disabled={true}
              />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#666",
                  marginTop: "4px",
                }}
              >
                Total capacity incentive in kWh. Calculated as the number of
                batteries multiplied by each battery's capacity incentive.
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#373533" }}
              >
                Total Usable Capacity (kWh)
              </Typography>
              <InfoIcon tooltipText="Automatically calculated: Number of Batteries × Nameplate Usable Capacity" />
            </Box>
            <FormInput
              name={"totalCapacity"}
              control={control}
              label={""}
              disabled={true}
            />
            <Typography
              sx={{
                fontSize: "11px",
                color: "#666",
                marginTop: "4px",
              }}
            >
              Total usable capacity in kWh. Calculated as the number of
              batteries multiplied by each battery's capacity.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <FormInput
            name={"nominalPower"}
            control={control}
            label={"Nominal Power (kW)"}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#373533" }}
              >
                Total Nominal Power (kW)
              </Typography>
              <InfoIcon tooltipText="Automatically calculated: Number of Batteries × Nominal Power" />
            </Box>
            <FormInput
              name={"totalNominalPower"}
              control={control}
              label={""}
              value={totalNominalPower}
              disabled={true}
            />
            <Typography
              sx={{
                fontSize: "11px",
                color: "#666",
                marginTop: "4px",
              }}
            >
              Total nominal power in kW. Calculated as the number of batteries
              multiplied by each battery's nominal power.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <IncentiveImpactHighlight
            impacted={isFieldImpacted("totalProjectCost")}
          >
            <FormCurrencyInput
              name={"totalProjectCost"}
              control={control}
              label={"Total project cost"}
              labelTooltip={
                "Sum of all costs for the design and installation of the solar and battery system, including but not limited to materials, labor, permitting, and, if applicable, financing costs."
              }
              placeholder={"Enter total cost"}
              value={totalProjectCostValue || ""}
              controlProps={{
                rules: {
                  required: "Required",
                  validate: (v: string) =>
                    Number(v) > 0 || "Amount must be greater than 0",
                },
              }}
              helperText={null}
            />
          </IncentiveImpactHighlight>
        </Grid>
        {selectedOemRequiresTcs && batteryProductName && (
          <Grid item xs={12}>
            <FormFileUpload
              name="termsAndConditionsFile"
              control={control}
              label="Terms and Conditions Document"
              controlProps={{
                rules: {
                  required: "Terms and conditions document is required",
                },
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormDatePicker
            name={"estimated_installation_date"}
            control={control}
            label={"Estimated Installation Date"}
          />
        </Grid>
        {!isContractor && (
          <Grid item xs={12}>
            <FormDatePicker
              name={"operationalizedDate"}
              control={control}
              label={"Operationalized Date"}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default SystemInformationStep;
